import { memo, useCallback, useMemo, useRef } from 'react';
import { setupI13n } from 'react-i13n';
import { connect } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useThemeClassName } from '@tinderapp/ui-react';

import config, { isEnabled } from 'Config';
import cssVariables from 'Lib/cssVariables';
import I13nTinderEvents from 'Lib/eventsSDK/i13nTinderEvents';
import ReactI13nBugsnag from 'Lib/i13n/ReactI13nBugsnag';
import ReactI13nGTag from 'Lib/i13n/ReactI13nGTag';
import getSvgFillManagerInstance from 'Lib/SvgFillManager';
import { ThemeGlobalStyles } from 'Lib/UiEngine';
import { cleanPath } from 'Lib/utils/pathUtils';
import shallowEqualNested from 'Lib/utils/shallowEqualNested';
import useI13n from 'Hooks/useI13n';
import useIsomorphicLayoutEffect from 'Hooks/useIsomorphicLayoutEffect';

import { getIs3DSComplete, getIsActiveTab } from 'Store/app/selectors';
import { getIsMobileLayout, getLayout } from 'Store/appSettings/selectors';
import { getPaResponse } from 'Store/context/selectors';
import { appInitializeComplete } from 'Store/lifecycles';
import { getIsAppLoading } from 'Store/loading/selectors';
import { selectHasCurrentModal } from 'Store/modals/selectors';
import { getShowPrivacyPrompt } from 'Store/privacy/selectors';
import { getCurrentPath, getPathName } from 'Store/routing/selectors';
import { getIsUserLoggedIn } from 'Store/user/selectors';

import MobileLayout from 'Containers/App/Layout/Mobile';
import Meta from 'Containers/Meta';
import Loadable from 'Components/AsyncComponent';
import ErrorBoundary from 'Components/ErrorBoundary/ErrorBoundaryHandler';
import LoadingScreen from 'Components/LoadingScreen';

import useAddToHomeHandle from './hooks/useAddToHomeHandle';
import useBotDetect from './hooks/useBotDetect';
import useDeeplinkIntent from './hooks/useDeeplinkIntent';
import useErrorTracking from './hooks/useErrorTracking';
import useHorizontalLock from './hooks/useHorizontalLock';
import useIdleTracker from './hooks/useIdleTracker';
import useInstrumentation from './hooks/useInstrumentation';
import useKeyboardManager from './hooks/useKeyboardManager';
import useLayoutChange from './hooks/useLayoutChange';
import useLoadingScreen from './hooks/useLoadingScreen';
import usePerfTracking from './hooks/usePerfTracking';
import usePreloadManager from './hooks/usePreloadManager';
import useRouteInfo from './hooks/useRouteInfo';
import useRouteNavigate from './hooks/useRouteNavigate';
import useUpdatePushText from './hooks/useUpdatePushText';
// eslint-disable-next-line import/first,import/no-unresolved
import 'atomic.css';
import '../../styles/sanitize.css';
import '../../styles/base.scss';
import './App.scss';

/**
 * Deferred bundle
 */
const DesktopLayout = Loadable({
  loader: () => import('Containers/App/Layout/Desktop' /* webpackChunkName: "pc-DesktopLayout" */),
  name: 'DesktopLayout',
});

const PrivacyPreferences = Loadable({
  loader: () =>
    import('Containers/PrivacyPreferences' /* webpackChunkName: "pc-PrivacyPreferences" */),
  name: 'PrivacyPreferences',
});

const HorizontalLock = Loadable({
  loader: () => import('Containers/HorizontalLock' /* webpackChunkName: "HorizontalLock" */),
  name: 'HorizontalLock',
});

const DeactivatedTabScreen = Loadable({
  loader: () =>
    import('Components/DeactivatedTabScreen' /* webpackChunkName: "DeactivatedTabScreen" */),
  name: 'DeactivatedTabScreen',
});

const ModalManager = Loadable({
  loader: () => import('Containers/ModalManager' /* webpackChunkName: "pc-ModalManager" */),
  name: 'ModalManager',
});

const NotificationManager = Loadable({
  loader: () => import('Containers/Notification' /* webpackChunkName: "NotificationManager" */),
  name: 'NotificationManager',
});

const Onboarding = Loadable({
  loader: () => import('Containers/Onboarding' /* webpackChunkName: "pc-Onboarding" */),
  name: 'Onboarding',
});

const ErrorDisplayComponent = Loadable({
  loader: () =>
    import(
      'Components/ErrorBoundary/ErrorDisplayComponent' /* webpackChunkName: "ErrorDisplayComponent" */
    ),
  name: 'ErrorDisplayComponent',
});

const Favicon = Loadable({
  loader: () => import('Containers/Favicon' /* webpackChunkName: "Favicon" */),
  name: 'Favicon',
});

const NewMatchOrMessageCounter = Loadable({
  loader: () =>
    import(
      'Containers/NewMatchOrMessageCounter' /* webpackChunkName: "NewMatchOrMessageCounter" */
    ),
  name: 'NewMatchOrMessageCounter',
});

const GlobalDescription = Loadable({
  loader: () =>
    import('Components/A11y/GlobalDescription' /* webpackChunkName: "GlobalDescription" */),
  name: 'GlobalDescription',
});

// Instrumentation plugin
const reactI13nGTag = new ReactI13nGTag();
const reactI13nBugsnag = new ReactI13nBugsnag();
const i13nTinderEvents = new I13nTinderEvents();

// SVG Fill Manager
const { ManagedSvgFillLibrary } = getSvgFillManagerInstance();

type AppProps = {
  activeTab: boolean;
  currentPath?: string | undefined;
  identifier3ds: string | undefined;
  initialized: boolean;
  is3dsComplete: boolean;
  isMobile: boolean;
  layout: string;
  loading: boolean;
  loggedIn: boolean;
  pathname?: string | undefined;
  showPrivacyPrompt: boolean;
  hasCurrentModal: boolean;
};

const App = (props: AppProps) => {
  const {
    activeTab,
    currentPath = __CLIENT__ ? cleanPath(window.location.pathname) : undefined,
    identifier3ds = '',
    initialized,
    is3dsComplete = false,
    isMobile,
    loading,
    loggedIn,
    showPrivacyPrompt,
    hasCurrentModal,
  } = props;

  const { pageTransitionDuration } = cssVariables;
  const shouldShowPrivacyBanner = showPrivacyPrompt && isEnabled('cookiePolicy');
  const { executeEvent } = useI13n();
  const nodeRef = useRef(null);

  const { showLoadingScreen } = useLoadingScreen({
    identifier3ds,
    initialized,
    is3dsComplete,
    loading,
  });
  // Set route path
  useRouteInfo({ loading });
  // handle service worker deeplinking
  useDeeplinkIntent();

  // Send page view
  useInstrumentation({
    executeEvent,
    path: currentPath,
  });

  // HorizontalLock
  const { horizontalLock } = useHorizontalLock({
    isMobile,
  });

  const errorTracker = useErrorTracking();

  // Layout detect
  useLayoutChange();
  // handle navigation outside of React context
  useRouteNavigate();

  useAddToHomeHandle({ executeEvent });
  useIdleTracker({ loggedIn, executeEvent });
  useKeyboardManager({ isMobile });
  usePerfTracking({ executeEvent });
  usePreloadManager();
  useUpdatePushText({ loggedIn });
  useBotDetect({ executeEvent });

  // Apply the theme classname to the body element
  const currentThemeClassName = useThemeClassName();
  useIsomorphicLayoutEffect(() => {
    if (document.body) {
      document.body.classList.add(currentThemeClassName);
      return () => {
        if (document.body) {
          document.body.classList.remove(currentThemeClassName);
        }
      };
    }
  }, [currentThemeClassName]);

  const layoutComponent = useMemo(() => {
    const LayoutComponent = isMobile ? MobileLayout : DesktopLayout;
    return (
      <div className="App__body H(100%) Pos(r) Z(0)">
        <LayoutComponent />
      </div>
    );
  }, [isMobile]);

  const globalComponent = useMemo(
    () => (
      <>
        {shouldShowPrivacyBanner && !hasCurrentModal && <PrivacyPreferences />}
        {loggedIn && <Onboarding />}
        <NotificationManager />
        <ModalManager />
        <ManagedSvgFillLibrary />
        {loggedIn && <Favicon />}
        <GlobalDescription />
      </>
    ),
    [hasCurrentModal, loggedIn, shouldShowPrivacyBanner]
  );

  const AppComponent = useMemo(() => {
    if (!activeTab) {
      return DeactivatedTabScreen;
    } else if (showLoadingScreen) {
      return LoadingScreen;
    } else if (horizontalLock) {
      return HorizontalLock;
    }
    return null;
  }, [activeTab, horizontalLock, showLoadingScreen]);

  const errorCallback = useCallback((error) => errorTracker?.handleError(error), [errorTracker]);

  const renderedChild = useMemo(
    () => (
      <>
        <Meta />
        <ThemeGlobalStyles />
        {loggedIn ? <NewMatchOrMessageCounter /> : null}
        <TransitionGroup component={null}>
          <CSSTransition
            key={[activeTab, showLoadingScreen].join('-')}
            classNames="animation-fade-in-out"
            timeout={{ enter: pageTransitionDuration, exit: pageTransitionDuration }}
            nodeRef={nodeRef}
          >
            {AppComponent ? (
              <AppComponent ref={nodeRef} />
            ) : (
              <div className="App Expand Ovy(a) Ovx(h)" ref={nodeRef}>
                {layoutComponent}
                {globalComponent}
              </div>
            )}
          </CSSTransition>
        </TransitionGroup>
      </>
    ),
    [
      loggedIn,
      activeTab,
      showLoadingScreen,
      pageTransitionDuration,
      AppComponent,
      layoutComponent,
      globalComponent,
    ]
  );

  return (
    <ErrorBoundary
      errorComponent={__DEVELOPMENT__ ? ErrorDisplayComponent : undefined}
      onError={errorCallback}
    >
      {renderedChild}
    </ErrorBoundary>
  );
};

const { rootModelData } = config.i13n;
const I13nApp = setupI13n(App, { rootModelData }, [
  i13nTinderEvents.getPlugin(),
  reactI13nGTag.getPlugin(),
  reactI13nBugsnag.getPlugin(),
]);

function mapStateToProps(state) {
  return {
    activeTab: getIsActiveTab(state),
    currentPath: getCurrentPath(state),
    identifier3ds: getPaResponse(state),
    initialized: appInitializeComplete.isTriggered(state),
    is3dsComplete: getIs3DSComplete(state),
    isMobile: getIsMobileLayout(state),
    layout: getLayout(state),
    loading: getIsAppLoading(state),
    loggedIn: getIsUserLoggedIn(state),
    pathname: getPathName(state),
    showPrivacyPrompt: getShowPrivacyPrompt(state),
    hasCurrentModal: selectHasCurrentModal(state),
  };
}

export default connect(mapStateToProps)(
  memo(I13nApp, (prevProps, nextProps) => {
    const ignoreProps = ['i13n'];
    const areEqual = shallowEqualNested(nextProps, prevProps, ignoreProps, false);

    return areEqual;
  })
);
